import { useState, useEffect } from "react";
import { tradeService } from "../../services/tradeService";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useToast } from "../ui/ToastContainer";
import {
  parseApiError,
  formatValidationErrors,
} from "../../utils/errorHandler";
import { Trade } from "./TradeTable";
import { uploadImage } from "../../services/uploadService";
import {
  Image as ImageIcon,
  Upload,
  Loader2,
  X,
  FolderOpen,
} from "lucide-react";

interface AddEditTradeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  trade?: Trade | null;
}

export function AddEditTradeDialog({
  isOpen,
  onClose,
  onSuccess,
  trade,
}: AddEditTradeDialogProps) {
  const isEdit = !!trade;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "", // URL or file path concept, simplified as string for now
    thumbnail: "",
    order: 0,
    metaTitle: "",
    metaDescription: "",
    keywords: "", // Comma separated
    isActive: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (isOpen) {
      if (trade) {
        setFormData({
          name: trade.name,
          description: trade.description || "",
          icon: trade.icon || "",
          thumbnail: trade.thumbnail || "",
          order: trade.order || 0,
          metaTitle: (trade as any).metaTitle || "", // casting as any if field missing in Interface
          metaDescription: (trade as any).metaDescription || "",
          keywords: (trade as any).keywords
            ? (trade as any).keywords.join(", ")
            : "",
          isActive: trade.isActive,
        });
      } else {
        // Reset for Add
        setFormData({
          name: "",
          description: "",
          icon: "",
          thumbnail: "",
          order: 0,
          metaTitle: "",
          metaDescription: "",
          keywords: "",
          isActive: true,
        });
      }
      setError(null);
      setValidationErrors({});
    }
  }, [isOpen, trade]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadImage(file, "trades");
      setFormData((prev) => ({ ...prev, thumbnail: url }));
      showSuccess("Thumbnail uploaded successfully", "Upload Success");
    } catch (err: any) {
      console.error("Failed to upload image:", err);
      showError(err.message || "Failed to upload image", "Upload Failed");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadImage(file, "trades/icons");
      setFormData((prev) => ({ ...prev, icon: url }));
      showSuccess("Icon uploaded successfully", "Upload Success");
    } catch (err: any) {
      console.error("Failed to upload icon:", err);
      showError(err.message || "Failed to upload image", "Upload Failed");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setValidationErrors({});

    try {
      // Process keywords into array
      const processedData = {
        ...formData,
        keywords: formData.keywords
          .split(",")
          .map((k) => k.trim())
          .filter((k) => k),
        order: Number(formData.order),
      };

      if (isEdit && trade) {
        await tradeService.updateTrade(trade._id, processedData);
        showSuccess("Trade updated successfully", "Success");
      } else {
        await tradeService.createTrade(processedData);
        showSuccess("Trade created successfully", "Success");
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Save trade error:", err);
      const apiError = parseApiError(err);

      if (apiError.errors && Object.keys(apiError.errors).length > 0) {
        setValidationErrors(apiError.errors);
        setError("Please fix the validation errors below.");
      } else {
        setError(apiError.message);
      }

      showError(apiError.message, isEdit ? "Update Failed" : "Create Failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Trade" : "Add New Trade"}
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4 max-h-[70vh] overflow-y-auto pr-2"
      >
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            <p className="font-medium">{error}</p>
            {Object.keys(validationErrors).length > 0 && (
              <ul className="mt-2 ml-4 list-disc space-y-1 text-xs">
                {formatValidationErrors(validationErrors).map((err, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{err.field}:</span>{" "}
                    {err.messages.join(", ")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2 col-span-2">
            <label htmlFor="name" className="text-sm font-medium text-gray-700">
              Trade Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="name"
              placeholder="e.g. Project Management"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="space-y-2 col-span-2">
            <label
              htmlFor="description"
              className="text-sm font-medium text-gray-700"
            >
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors"
              placeholder="Brief description of this trade..."
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="order"
              className="text-sm font-medium text-gray-700"
            >
              Display Order
            </label>
            <Input
              id="order"
              type="number"
              placeholder="0"
              value={formData.order}
              onChange={handleChange}
            />
          </div>

          {/* Icon Upload */}
          <div className="space-y-2">
            <label htmlFor="icon" className="text-sm font-medium text-gray-700">
              Icon
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="icon"
                  placeholder="https://..."
                  value={formData.icon}
                  onChange={handleChange}
                  disabled={isUploading}
                  icon={
                    formData.icon ? (
                      <div className="w-5 h-5 rounded overflow-hidden">
                        <img
                          src={formData.icon}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <FolderOpen size={16} />
                    )
                  }
                />
              </div>
              <div className="relative">
                <input
                  type="file"
                  id="icon-upload"
                  className="hidden"
                  accept="image/*"
                  onChange={handleIconUpload}
                  disabled={isUploading || isLoading}
                />
                <label
                  htmlFor="icon-upload"
                  className={`h-full px-3 flex items-center justify-center bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg cursor-pointer border border-gray-200 transition-colors ${
                    isUploading || isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  title="Upload Icon"
                >
                  {isUploading ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <Upload size={16} />
                  )}
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-2 col-span-2">
            <label
              htmlFor="thumbnail"
              className="text-sm font-medium text-gray-700"
            >
              Thumbnail
            </label>
            <div className="space-y-3">
              {/* Preview */}
              {formData.thumbnail && (
                <div className="relative w-full h-40 bg-gray-50 rounded-lg overflow-hidden border border-gray-200 group">
                  <img
                    src={formData.thumbnail}
                    alt="Thumbnail Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, thumbnail: "" })}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="thumbnail"
                    placeholder="https://..."
                    value={formData.thumbnail}
                    onChange={handleChange}
                    disabled={isUploading}
                    icon={<ImageIcon size={16} />}
                  />
                </div>
                <div className="relative">
                  <input
                    type="file"
                    id="thumbnail-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploading || isLoading}
                  />
                  <label
                    htmlFor="thumbnail-upload"
                    className={`h-full px-4 flex items-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg cursor-pointer border border-gray-200 transition-colors ${
                      isUploading || isLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isUploading ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Upload size={18} />
                    )}
                    <span className="text-sm font-medium">Upload</span>
                  </label>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Recommended size: 1280x720px. Max size 5MB.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 mt-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">
            SEO Settings
          </h4>

          <div className="space-y-3">
            <div className="space-y-2">
              <label
                htmlFor="metaTitle"
                className="text-sm font-medium text-gray-700"
              >
                Meta Title
              </label>
              <Input
                id="metaTitle"
                placeholder="SEO Title"
                value={formData.metaTitle}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="metaDescription"
                className="text-sm font-medium text-gray-700"
              >
                Meta Description
              </label>
              <textarea
                id="metaDescription"
                rows={2}
                className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors"
                placeholder="SEO Description..."
                value={formData.metaDescription}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="keywords"
                className="text-sm font-medium text-gray-700"
              >
                Keywords
              </label>
              <Input
                id="keywords"
                placeholder="Comma separated keywords"
                value={formData.keywords}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            className="w-32"
          >
            {isLoading ? "Saving..." : isEdit ? "Update Trade" : "Create Trade"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
